import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  Output,
} from '@angular/core';
import { Omnibox } from '@local/client-contracts';
import { DatePickerType } from '@local/common-web';
import { getModifiers, isKey, keyCodes } from '@local/ts-infra';
import moment from 'moment';

export type removalMethod = 'keydown' | 'click';
export type SelectedCustom = {
  dates: string[];
  type: DatePickerType;
};

@Component({
  selector: 'suggestion-tag',
  templateUrl: './suggestion-tag.component.html',
  styleUrls: ['./suggestion-tag.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TagComponent {
  private readonly dateFormatToDisplay = 'MMM D, YYYY';
  private readonly dateShortFormatToDisplay = 'MMM D';
  private _model: Omnibox.Tag;
  isCustomDate: boolean;
  selectedCustomValue: SelectedCustom;
  customRange: string[] = [];
  EXCLUDED_TAGS = ['account', 'app', 'type', 'service'];

  @Input() disabled: boolean;
  @HostBinding('class.removable')
  @Input()
  removable: boolean = true;
  @HostBinding('class.clickable')
  @Input()
  clickable: boolean = false;
  @Input() selected: boolean;
  @Input() lightOverlay: boolean;
  @Input() set model(val: Omnibox.Tag) {
    this._model = val;
    this.isCustomDate = val?.data?.customDate;
    if (this.isCustomDate) this.initCustomDate(val);
  }

  @Output() remove: EventEmitter<Omnibox.Tag> = new EventEmitter<Omnibox.Tag>();

  get model() {
    return this._model;
  }

  private initCustomDate(val: Omnibox.Tag) {
    const value = val?.title.split(':');
    const type = value[0] as DatePickerType;
    let dates = value[1].split('-');
    if (type !== 'range') {
      dates = dates.filter((d) => d !== 'undefined');
    }
    this.selectedCustomValue = {
      type,
      dates: dates.map((d) => {
        return d !== 'undefined' ? moment(d).format(this.dateFormatToDisplay) : null;
      }),
    };
    if (type === 'range') {
      this.initCustomRange(this.selectedCustomValue.dates);
    }
  }

  private initCustomRange(dates: string[]) {
    this.customRange = [];
    const start = moment(dates[0]);
    const end = moment(dates[1]);
    if (start?.year() !== end?.year()) return;

    this.customRange.push(start.format(this.dateShortFormatToDisplay), end.format(this.dateFormatToDisplay));
  }

  onRemove() {
    this.remove.emit(this.model);
  }
  constructor(public ref: ElementRef, private cdr: ChangeDetectorRef) {}

  @HostBinding('class.selected') get isSelected() {
    return this.selected;
  }

  @HostBinding('class.light-overlay') get lightOverlayClass() {
    return this.lightOverlay;
  }

  @HostListener('focus', ['$event'])
  onFocus(event: KeyboardEvent): void {
    this.selected = true;
    this.cdr.markForCheck();
  }

  @HostListener('blur', ['$event'])
  onBlur(event: KeyboardEvent): void {
    this.selected = false;
    this.cdr.markForCheck();
  }
  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (this.selected && getModifiers(event).length === 0 && (isKey(event, keyCodes.backspace) || isKey(event, keyCodes.delete))) {
      this.onRemove();
    }
  }
}
